import React, { useEffect, useState, useCallback } from "react";
import { useOrganisations } from "../../Auth/useOrganisations";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import GifLoader from "../../Components/loader";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { getAllCashJournals } from "../../Services/JournalApi";
import { useNavigate } from "react-router-dom";
import { getUsers } from "../../Services/UserApi";
import { toast } from "react-toastify";

const ROWS_PER_PAGE = 10;

function JournauxCaisse() {
  const { t } = useTranslation();
  const [journaux, setJournaux] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [organisations, setOrganisations, usedOrganisations] =
    useOrganisations();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);

  // ── Draft filter state (what user is typing) ──────────────────────────────
  const [startDate, setStartDate] = useState(
    moment().startOf("day").toISOString(),
  );
  const [endDate, setEndDate] = useState(moment().endOf("day").toISOString());
  const [filters, setFilters] = useState({
    caissier: "",
    cloture: "",
    valide: "",
  });

  // ── Applied filter state (what actually filters the table) ────────────────
  const [appliedStartDate, setAppliedStartDate] = useState(
    moment().startOf("day").toISOString(),
  );
  const [appliedEndDate, setAppliedEndDate] = useState(
    moment().endOf("day").toISOString(),
  );
  const [appliedFilters, setAppliedFilters] = useState({
    caissier: "",
    cloture: "",
    valide: "",
  });

  // ── API load (uses applied dates) ─────────────────────────────────────────
  const loadJournaux = useCallback(async () => {
    if (!usedOrganisations) return;
    try {
      setLoading(true);
      const response = await getAllCashJournals(
        usedOrganisations,
        appliedStartDate,
        appliedEndDate,
      );
      if (response.data.success !== "0") {
        setJournaux(response.data.data || []);
      }
    } catch (error) {
      console.error("Error loading journals:", error);
    } finally {
      setLoading(false);
    }
  }, [usedOrganisations, appliedStartDate, appliedEndDate]);

  useEffect(() => {
    loadJournaux();
  }, [loadJournaux]);
  const loadUsers = useCallback(async () => {
    if (!usedOrganisations) return;
    try {
      const response = await getUsers(usedOrganisations);
      if (response.data.success) {
        setUsers(response.data.data || []);
      }
    } catch (error) {
      console.error("Error loading users:", error);
      toast.error("Erreur lors du chargement des utilisateurs");
    }
  }, [usedOrganisations]);
  useEffect(() => {
    loadUsers();
  }, [loadUsers]);
  // ── Reset page when applied filters change ────────────────────────────────
  useEffect(() => {
    setCurrentPage(0);
  }, [appliedFilters, appliedStartDate, appliedEndDate]);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleApplyFilters = () => {
    setAppliedFilters({ ...filters });
    setAppliedStartDate(startDate);
    setAppliedEndDate(endDate);
    // loadJournaux will re-run automatically via useEffect on appliedStartDate/appliedEndDate
  };

  const handleResetFilters = () => {
    const empty = { caissier: "", cloture: "", valide: "" };
    const todayStart = moment().startOf("day").toISOString();
    const todayEnd = moment().endOf("day").toISOString();

    setFilters(empty);
    setAppliedFilters(empty);
    setStartDate(todayStart);
    setEndDate(todayEnd);
    setAppliedStartDate(todayStart);
    setAppliedEndDate(todayEnd);
  };

  // ── Client-side filtering (uses applied state) ────────────────────────────
  const filteredJournaux = journaux.filter((j) => {
    const caissierName = (
      (j.user_id?.nom || "") +
      " " +
      (j.user_id?.prenom || "")
    ).toLowerCase();

    if (
      appliedFilters.caissier &&
      !caissierName.includes(appliedFilters.caissier.toLowerCase())
    )
      return false;
    if (appliedFilters.cloture === "oui" && !j.cloture) return false;
    if (appliedFilters.cloture === "non" && j.cloture) return false;
    if (appliedFilters.valide === "oui" && !j.valide) return false;
    if (appliedFilters.valide === "non" && j.valide) return false;

    return true;
  });

  // ── Total ecart across all filtered journaux (not just the current page) ──
  const totalEcart = filteredJournaux.reduce(
    (sum, j) => sum + (Number(j.ecart) || 0),
    0,
  );

  // ── Pagination ────────────────────────────────────────────────────────────
  const pageCount = Math.ceil(filteredJournaux.length / ROWS_PER_PAGE);
  const paginatedJournaux = filteredJournaux.slice(
    currentPage * ROWS_PER_PAGE,
    currentPage * ROWS_PER_PAGE + ROWS_PER_PAGE,
  );

  return (
    <section className="content" style={{ padding: "5px 7px 0px 6px" }}>
      <GifLoader loading={loading} />

      <div className="container-full">
        <div className="content-header">
          <div className="d-flex align-items-center">
            <div className="mr-auto">
              <h3 className="page-title">{t("Liste des journaux")}</h3>
            </div>
          </div>
        </div>

        <section className="content">
          {/* ── Filter Card ── */}
          <div className="card mb-4">
            <div className="card-body">
              <div className="row align-items-end">
                {/* Date début */}
                <div className="col-md-2 mb-2">
                  <div className="form-group mb-0">
                    <label>{t("Date de début")}</label>
                    <DatePicker
                      className="form-control"
                      selected={moment(startDate).toDate()}
                      onChange={(date) =>
                        setStartDate(moment(date).startOf("day").toISOString())
                      }
                      selectsStart
                      startDate={moment(startDate).toDate()}
                      endDate={moment(endDate).toDate()}
                      dateFormat="dd-MM-yyyy"
                      placeholderText={t("Date de début")}
                    />
                  </div>
                </div>

                {/* Date fin */}
                <div className="col-md-2 mb-2">
                  <div className="form-group mb-0">
                    <label>{t("Date de fin")}</label>
                    <DatePicker
                      className="form-control"
                      selected={moment(endDate).toDate()}
                      onChange={(date) =>
                        setEndDate(moment(date).endOf("day").toISOString())
                      }
                      selectsEnd
                      startDate={moment(startDate).toDate()}
                      endDate={moment(endDate).toDate()}
                      minDate={moment(startDate).toDate()}
                      dateFormat="dd-MM-yyyy"
                      placeholderText={t("Date de fin")}
                    />
                  </div>
                </div>

                {/* Caissier */}
                <div className="col-md-2 mb-2">
                  <div className="form-group">
                    <label>Caissier</label>
                    <select
                      className="form-control"
                      value={filters.caissier}
                      onChange={(e) =>
                        handleFilterChange("caissier", e.target.value)
                      }
                    >
                      <option value="">Tous les caissiers</option>
                      {users
                        .filter((user) => user.role === "caissier")
                        .map((caissier) => (
                          <option key={caissier._id} value={caissier._id}>
                            {caissier.nom} {caissier.prenom}
                          </option>
                        ))}
                    </select>
                  </div>
                </div>

                {/* Cloturé */}
                <div className="col-md-2 mb-2">
                  <div className="form-group mb-0">
                    <label>{t("Cloturé")}</label>
                    <select
                      className="form-control"
                      value={filters.cloture}
                      onChange={(e) =>
                        handleFilterChange("cloture", e.target.value)
                      }
                    >
                      <option value="">{t("Tous")}</option>
                      <option value="oui">{t("Oui")}</option>
                      <option value="non">{t("Non")}</option>
                    </select>
                  </div>
                </div>

                {/* Validé */}
                <div className="col-md-2 mb-2">
                  <div className="form-group mb-0">
                    <label>{t("Validé")}</label>
                    <select
                      className="form-control"
                      value={filters.valide}
                      onChange={(e) =>
                        handleFilterChange("valide", e.target.value)
                      }
                    >
                      <option value="">{t("Tous")}</option>
                      <option value="oui">{t("Oui")}</option>
                      <option value="non">{t("Non")}</option>
                    </select>
                  </div>
                </div>

                {/* Action buttons — kept inline with the filters, not on their own row */}
                <div className="col-md-2 mb-2">
                  <div className="form-group mb-0">
                    <label>&nbsp;</label>
                    <div className="d-flex" style={{ gap: "8px" }}>
                      <button
                        className="btn btn-primary"
                        onClick={handleApplyFilters}
                        title={t("Rechercher")}
                      >
                        <i className="fa fa-search" />
                      </button>
                      <button
                        className="btn btn-outline-secondary"
                        onClick={handleResetFilters}
                        title={t("Réinitialiser")}
                      >
                        <i className="fas fa-undo"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div className="row">
                <div className="col-12 text-right">
                  <span className="text-muted" style={{ fontSize: "13px" }}>
                    {filteredJournaux.length} {t("journal(ux) trouvé(s)")}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* ── Table ── */}
          <div className="row">
            <div className="col-12">
              <div className="box">
                <div className="box-body p-0">
                  <div className="table-responsive rounded card-table">
                    <table className="table border-no" id="orders-table">
                      <thead>
                        <tr>
                          <th>{t("Code")}</th>
                          <th>{t("Caissier/Serveur")}</th>
                          <th>{t("Fond initial")}</th>
                          <th>{t("Cloturé")}</th>
                          <th>{t("Fond final")}</th>
                          <th>{t("Ecart")}</th>
                          <th>{t("Validé")}</th>
                          <th>{t("Validé par")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedJournaux.length > 0 ? (
                          paginatedJournaux.map((journal) => (
                            <tr
                              key={journal?._id}
                              className="hover-primary"
                              style={{ cursor: "pointer" }}
                              onClick={() =>
                                navigate("/journal/" + journal?.id)
                              }
                            >
                              <td>{journal?.code || "N/A"}</td>
                              <td>
                                {journal?.user_id
                                  ? journal.user_id.nom +
                                    " " +
                                    journal.user_id.prenom
                                  : "N/A"}
                              </td>
                              <td>{journal?.fond_initial ?? "N/A"}</td>
                              <td>
                                {journal?.cloture ? (
                                  <span className="badge badge-success">
                                    Oui
                                  </span>
                                ) : (
                                  <span className="badge badge-secondary">
                                    Non
                                  </span>
                                )}
                              </td>
                              <td>{journal?.fond_final ?? "-"}</td>
                              <td>{journal?.ecart?.toFixed(3) ?? "-"}</td>
                              <td>
                                {journal?.valide ? (
                                  <span className="badge badge-success">
                                    Oui
                                  </span>
                                ) : (
                                  <span className="badge badge-secondary">
                                    Non
                                  </span>
                                )}
                              </td>
                              <td
                                onClick={(e) => {
                                  e.stopPropagation();
                                  console.log(journal);
                                }}
                              >
                                {journal?.validator_id
                                  ? journal.validator_id.nom +
                                    " " +
                                    journal.validator_id.prenom
                                  : "-"}
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="8" className="text-center py-4">
                              {loading
                                ? t("Chargement...")
                                : t("Aucun journal trouvé")}
                            </td>
                          </tr>
                        )}
                      </tbody>
                      {filteredJournaux.length > 0 && (
                        <tfoot>
                          <tr style={{ background: "#f8f9fa", fontWeight: 700 }}>
                            <td colSpan="5">
                              {t("Total écart")}
                            </td>
                            <td>{totalEcart.toFixed(3)}</td>
                            <td colSpan="2"></td>
                          </tr>
                        </tfoot>
                      )}
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ── Pagination ── */}
          {pageCount > 1 && (
            <div className="row mt-3">
              <div
                className="col-12 d-flex justify-content-center align-items-center"
                style={{ gap: "4px" }}
              >
                <button
                  className="btn btn-sm btn-outline-secondary"
                  disabled={currentPage === 0}
                  onClick={() => setCurrentPage((p) => p - 1)}
                >
                  &lsaquo;
                </button>
                {Array.from({ length: pageCount }, (_, i) => (
                  <button
                    key={i}
                    className={`btn btn-sm ${
                      currentPage === i
                        ? "btn-primary"
                        : "btn-outline-secondary"
                    }`}
                    onClick={() => setCurrentPage(i)}
                  >
                    {i + 1}
                  </button>
                ))}
                <button
                  className="btn btn-sm btn-outline-secondary"
                  disabled={currentPage === pageCount - 1}
                  onClick={() => setCurrentPage((p) => p + 1)}
                >
                  &rsaquo;
                </button>
              </div>
            </div>
          )}
        </section>
      </div>
    </section>
  );
}

export default JournauxCaisse;
